
/**
 * Utility functions for handling and displaying errors
 */

/**
 * Formats an error into a human-readable message with additional debug information
 */
export const formatErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
};

/**
 * KB-3033: the copy for the three auth failures whose cause is the user's own
 * browser environment, and which are therefore the ones the user can actually
 * fix — if we tell them what to do.
 *
 * Exported so tests assert the real strings rather than a duplicated copy, and
 * so `src/lib/auth-transport.ts` can assert its timeout message stays
 * DISTINGUISHABLE from `NETWORK_REQUEST_FAILED` (a request that was refused and
 * a request that never answered are different facts and must not print the same
 * sentence).
 *
 * Why this matters more than usual: before this, `auth/network-request-failed`
 * fell to the `default:` arm and produced "Login failed: Authentication error:
 * Firebase: Error (auth/network-request-failed)." — true, and useless. It sent
 * an experienced operator to the CSP, then to App Check, then to a whole
 * dispatch loop, while the actual cause was a content blocker one toggle away.
 */
export const AUTH_ERROR_COPY = {
  /**
   * The request left the SDK and was stopped before it reached Google. Names
   * the three things that actually cause it, in rough order of likelihood.
   *
   * The private-window step leads the remedy because it is the cheapest real
   * test the user can run: extensions are off there by default, so it splits
   * "an extension is blocking this" from "something else is" in one click,
   * without asking anyone to hunt through a settings panel first.
   *
   * That ordering is evidence-led, not a guess. On the machine that produced
   * this report, `pluginkit -mAvvv -p com.apple.Safari.content-blocker`
   * returned NO matches — no content blocker was installed at all — while four
   * Safari *extensions* were enabled, one of them updated two days before the
   * failure. Copy that said only "turn off content blockers" would have sent
   * that user to look for something they did not have. Both are named, but the
   * remedy has to cover the one that was actually there.
   *
   * Round-2 O1: the private-window tip is now scoped to Chrome/Firefox. An
   * earlier version said "extensions are off there by default" flatly, which is
   * false in Safari — content blockers stay active in Private Browsing, and
   * extensions can be enabled there too. That is the browser the reporter was
   * using, so the one reader we know about was the one it would have misled.
   */
  NETWORK_REQUEST_FAILED:
    "Your browser couldn't reach Google's sign-in service. This is usually a browser extension " +
    'or content blocker, a VPN or iCloud Private Relay, or a dropped connection. Turn off ' +
    'extensions and content blockers for this site and reload — in Chrome or Firefox, a private ' +
    'window is a quick way to test that. If it still fails, turn off any VPN and try again.',

  /**
   * Identity Toolkit refused the App Check token (KB-3352).
   *
   * Every caller of `handleFirebaseError` renders this without any App Check
   * evidence, and the refused token may not have been a real one: after a 503
   * or a reCAPTCHA failure the SDK hands Auth a placeholder, which Identity
   * Toolkit then refuses (round-1 B2). So it says only what is true either way
   * — the check did not accept the attempt — and names no session refusal and
   * no blocker. `AuthContext.login` renders it too, except inside a refusal
   * throttle: a token-invalid while the SDK held a valid token means Identity
   * Toolkit refused a token Google had minted for this session, which is no
   * evidence the browser was refused (round-2 B4).
   *
   * The pre-KB-3352 text said "an extension or privacy setting is likely
   * interfering", which nothing about this code evidences.
   */
  APP_CHECK_TOKEN_INVALID:
    "Google's security check did not accept this sign-in. Reload the page and try again. If it " +
    'keeps happening, try a different browser or browser profile, or contact support@kidsbooked.com.',

  /**
   * The SDK's own catch-all. Deliberately does NOT guess at a cause: saying
   * "this is a content blocker" here would be asserting something we did not
   * verify, which is the exact failure this ticket exists to correct.
   */
  INTERNAL_ERROR:
    "Sign-in failed for an unexpected reason and we couldn't determine the cause. Please try " +
    'again in a moment. If it keeps happening, contact support@kidsbooked.com.',
} as const;

/**
 * Specific handler for Firebase related errors
 */
export const handleFirebaseError = (error: any): string => {
  // Handle Firebase auth errors
  if (error?.code?.startsWith('auth/')) {
    switch (error.code) {
      case 'auth/user-not-found':
        return 'User not found';
      case 'auth/wrong-password':
        return 'Incorrect password';
      case 'auth/invalid-credential':
        return 'Invalid login credentials';
      case 'auth/too-many-requests':
        return 'Too many login attempts. Please try again later';
      case 'auth/email-already-in-use':
        return 'Email already in use';
      // KB-3033: the browser-environment failures. Each states what the user
      // can do; none of them prints the raw SDK string.
      case 'auth/network-request-failed':
        return AUTH_ERROR_COPY.NETWORK_REQUEST_FAILED;
      case 'auth/firebase-app-check-token-is-invalid':
        return AUTH_ERROR_COPY.APP_CHECK_TOKEN_INVALID;
      case 'auth/internal-error':
        return AUTH_ERROR_COPY.INTERNAL_ERROR;
      default:
        // Retained deliberately (KB-3033 AC-2): an unknown code must still
        // surface its raw message rather than be flattened into a reassuring
        // generic one. Naming a cause we did not verify would be worse than
        // printing jargon.
        //
        // Marketing takes the opposite posture here — KB-1344, in
        // `src/components/app/auth/GoogleSignInButton.tsx`, never leaks the raw
        // Firebase string into user-facing copy and routes it to Sentry from
        // the catch instead. That is the better end state and is the precedent
        // for any follow-up that hardens this arm; it is deliberately NOT done
        // here, because KB-3033 AC-2 scopes this ticket to the three named
        // codes.
        return `Authentication error: ${error.message}`;
    }
  }
  
  // Handle Firebase Firestore errors
  if (error?.code?.startsWith('firestore/')) {
    return `Database error: ${error.message}`;
  }
  
  // Handle Firebase Storage errors
  if (error?.code?.startsWith('storage/')) {
    switch (error.code) {
      case 'storage/unauthorized':
        return 'You do not have permission to upload files to this location';
      case 'storage/canceled':
        return 'Upload canceled';
      case 'storage/quota-exceeded':
        return 'Storage quota exceeded';
      case 'storage/invalid-argument':
        return 'Invalid file format';
      default:
        return `Storage error: ${error.message}`;
    }
  }
  
  // Handle Cloud Functions errors
  if (error?.code?.startsWith('functions/')) {
    switch (error.code) {
      case 'functions/unauthenticated':
        return 'You must be authenticated to perform this action';
      case 'functions/permission-denied':
        return 'You do not have permission to perform this action';
      case 'functions/invalid-argument':
        return 'Invalid data provided to the function';
      default:
        return `Cloud function error: ${error.message}`;
    }
  }
  
  // Generic error handling
  return formatErrorMessage(error);
};

/**
 * Check if the error is related to CORS issues
 */
export const isCorsError = (error: unknown): boolean => {
  const errorString = String(error).toLowerCase();
  return (
    errorString.includes('cors') || 
    errorString.includes('blocked by') || 
    errorString.includes('access-control-allow-origin') ||
    errorString.includes('cross-origin')
  );
};

/**
 * Suggest fixes for common Firebase errors
 */
export const getSuggestionForError = (error: unknown): string | null => {
  const errorString = String(error).toLowerCase();
  
  if (isCorsError(error)) {
    return "This appears to be a CORS issue. Make sure your Cloud Function allows requests from this domain.";
  }
  
  if (errorString.includes('failed to fetch') || errorString.includes('network') || errorString.includes('connection')) {
    return "This appears to be a network issue. Check your internet connection and make sure the Cloud Function is accessible.";
  }
  
  if (errorString.includes('permission') || errorString.includes('unauthorized') || errorString.includes('unauthenticated')) {
    return "You may not have the required permissions. Ensure you're signed in as an admin user.";
  }
  
  if (errorString.includes('storage') && errorString.includes('unauthorized')) {
    return "You don't have permission to upload to this storage location. Check your Firebase Storage rules.";
  }
  
  return null;
};
